import { useSlotId } from '@react-aria/utils';
import type { AriaLabelingProps, DOMProps } from '@react-types/shared';
import type { ForwardedRef, ReactNode } from 'react';
import { Children, Fragment, forwardRef, isValidElement } from 'react';
import { DEFAULT_SLOT, Provider } from 'react-aria-components';

import {
  AlertFilledIcon,
  InformationFilledIcon,
  StatusWarningFilledIcon,
} from '@infinex/ui-icons';
import { IconContext } from '@infinex/ui-icons/context';
import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn } from '@infinex/ui-styles';
import { isReactText } from '@infinex/ui-utils/react';

import {
  ButtonContext,
  ButtonGroupContext,
  LinkButtonContext,
} from '../button';
import {
  CenterBaseline,
  Content,
  ContentContext,
  HeadingContext,
} from '../content';
import { Surface } from '../surface';

export type CalloutV2Props = {
  /**
   * The content of the callout.
   */
  children: ReactNode;
  /**
   * By default, the callout’s icon is informed by the `tone`. Use the `icon`
   * prop to render a custom icon that’s appropriate for the message.
   *
   * @default <InformationFilledIcon />
   */
  icon?: ReactNode;
  /**
   * The tone of the callout influences the background, text and icon colours.
   * Use `critical` for errors (e.g. a failed action the user must resolve).
   *
   * @default neutral
   */
  tone?: 'neutral' | 'caution' | 'critical';
  /**
   * Block callouts are generously spaced for standalone placement, adopting
   * compact spacing within containers narrower than 576px. Inline callouts
   * show a single line of message sized to its content — no heading or
   * actions.
   *
   * @default block
   */
  variant?: 'inline' | 'block';
} & AriaLabelingProps &
  DOMProps &
  BaseStyleProps &
  UnsafeStyleProps;

/**
 * Use callouts to highlight information that affects a section, feature or
 * page. Draw attention without interrupting users from their current task.
 *
 * Successor to `Callout`, which it will replace as adoption rolls out.
 */
export const CalloutV2 = forwardRef(function CalloutV2(
  props: CalloutV2Props,
  forwardedRef: ForwardedRef<HTMLElement>
) {
  const {
    children,
    className,
    icon,
    tone = 'neutral',
    variant = 'block',
    ...otherProps
  } = props;

  const { ariaLabel, ariaLabelledby, headingId } = useLabelProps(props);

  const iconElement = icon ?? ICON_MAP[tone];
  const toneClasses = TONE_CLASSES[tone];
  const isBlock = variant === 'block';
  const { actions, content } = partitionChildren(children);
  const hasActions = actions.length > 0;

  const sharedButtonContext = {
    slots: {
      // Buttons within an "actions" group resolve the default slot — the
      // group's slotted context doesn't reach them — so it carries the sizing
      // and static colour to keep grouped actions legible on the tone fills.
      [DEFAULT_SLOT]: { size: 'small', staticColor: 'white' },
      action: {
        prominence: 'high',
        size: 'small',
        staticColor: 'white',
        // ms-9 = icon width (20px) + gap (16px), aligning wrapped actions
        // with the text content rather than the icon
        className: cn(
          'ms-9 mt-4 justify-self-start',
          '@xl:col-start-2 @xl:ms-0 @xl:mt-0'
        ),
      },
    },
  } as const;

  return (
    <Surface
      {...otherProps}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      elementType="section"
      rounded="medium"
      ref={forwardedRef}
      className={cn(
        'grid min-w-0 grid-cols-[minmax(0,1fr)] items-center',
        isBlock
          ? cn(
              'px-4 py-3 @xl:gap-x-4 @xl:px-5 @xl:py-4',
              // callouts with actions keep the generous spacing at every size
              hasActions && 'gap-x-4 px-5 py-4'
            )
          : 'w-fit rounded-[20px] py-2 ps-3 pe-4',
        toneClasses.background,
        className
      )}
    >
      <Provider
        values={[
          [
            IconContext,
            {
              className: cn(
                toneClasses.icon,
                isBlock ? 'size-xsmall @xl:size-small' : 'size-2xsmall'
              ),
            },
          ],
          [
            ContentContext,
            {
              className: cn(
                'flex min-w-0 flex-col items-start gap-y-2',
                // text alignment to override any ancestor "text-center" declarations
                'text-start font-medium',
                isBlock ? 'text-sm' : 'text-xs',
                toneClasses.text
              ),
            },
          ],
          [
            HeadingContext,
            {
              className: cn(
                'text-start text-sm font-semibold',
                'not-last:mb-2',
                toneClasses.heading
              ),
              id: headingId,
            },
          ],
          [ButtonContext, sharedButtonContext],
          [LinkButtonContext, sharedButtonContext],
          [
            ButtonGroupContext,
            {
              slots: {
                [DEFAULT_SLOT]: {},
                actions: {
                  size: 'small',
                  staticColor: 'white',
                  // ps-9 = icon width (20px) + gap (16px), aligning wrapped
                  // actions with the text content rather than the icon
                  // (padding, not margin, so the full-width group doesn't
                  // overflow its grid cell)
                  className: cn(
                    'mt-4 ps-9',
                    '@xl:col-start-2 @xl:mt-0 @xl:flex-row-reverse @xl:ps-0'
                  ),
                },
              },
            },
          ],
        ]}
      >
        <div
          className={cn(
            // baseline-align the icon with the first line of text
            'flex min-w-0 items-baseline',
            isBlock
              ? cn('gap-x-3 text-sm @xl:gap-x-4', hasActions && 'gap-x-4')
              : 'gap-x-2 text-xs'
          )}
        >
          <CenterBaseline>{iconElement}</CenterBaseline>
          <div className="min-w-0 flex-1">
            {isReactText(children) ? <Content>{children}</Content> : content}
          </div>
        </div>
        {actions}
      </Provider>
    </Surface>
  );
});

// Ensures docs tooling renders `<CalloutV2>` rather than `<React.ForwardRef>`.
CalloutV2.displayName = 'CalloutV2';

// Utilities
// -----------------------------------------------------------------------------

const ICON_MAP = {
  neutral: <InformationFilledIcon />,
  caution: <StatusWarningFilledIcon />,
  critical: <AlertFilledIcon />,
};

const TONE_CLASSES = {
  neutral: {
    background: 'bg-surfaceTwo',
    heading: 'text-emphasis',
    icon: 'fill-default',
    text: 'text-default',
  },
  caution: {
    // #FFAA00 at 11.76% alpha — the amber scale has no translucent fill token
    background: 'bg-[#ffaa001e]',
    heading: 'text-cautionOnFill',
    icon: 'fill-cautionOnFill',
    text: 'text-cautionOnFill',
  },
  critical: {
    background: 'bg-fillCritical',
    heading: 'text-criticalOnFill',
    icon: 'fill-criticalOnFill',
    text: 'text-criticalOnFill',
  },
};

/**
 * Splits children into "action"/"actions" slot elements and everything else,
 * so the text content renders within a single cell that actions can align
 * against.
 */
function partitionChildren(node: ReactNode): {
  actions: ReactNode[];
  content: ReactNode[];
} {
  const actions: ReactNode[] = [];
  const content: ReactNode[] = [];

  const flatten = (input: ReactNode): ReactNode[] =>
    Children.toArray(input).flatMap((child) =>
      isValidElement(child) && child.type === Fragment
        ? flatten((child.props as { children?: ReactNode }).children)
        : [child]
    );

  const nodes = flatten(node);

  for (const child of nodes) {
    const slot = isValidElement(child)
      ? (child.props as { slot?: string }).slot
      : undefined;
    if (slot === 'action' || slot === 'actions') actions.push(child);
    else content.push(child);
  }

  return { actions, content };
}

const SYSTEM_LABELS = {
  neutral: 'Notice',
  caution: 'Caution',
  critical: 'Error',
};

/**
 * A valid "region" must have an accessible name, via either of the `aria-label`
 * or `aria-labelledby` attributes.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/region_role
 */
function useLabelProps(props: CalloutV2Props): {
  ariaLabel?: string;
  ariaLabelledby?: string;
  headingId?: string;
} {
  const headingId: string | undefined = useSlotId([props.children]);
  const {
    tone = 'neutral',
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby = headingId,
  } = props;

  if (ariaLabel) return { ariaLabel, headingId };
  if (ariaLabelledby) return { ariaLabelledby, headingId };

  return { ariaLabel: SYSTEM_LABELS[tone], headingId };
}
